import { el, clear, toast } from '../core/dom.js';
import { getState } from '../core/state.js';
import { clearAll, settings as settingsStore } from '../storage/db.js';
import { navigate } from '../router.js';
import { downloadDiagnostics } from '../storage/diagnostics.js';
import { estimateStorage, isPersisted, requestPersistence } from '../storage/persistence.js';
import { notificationsSupported, requestPermission } from '../settings/notifications.js';
import { fullscreenSupported, isFullscreen, isInstallAvailable, isStandalone, promptInstall, toggleFullscreen } from '../settings/fullscreen.js';
import { track, setUserProp } from '../analytics/ga.js';
import type { UserSettings } from '../core/types.js';

// Settings keys whose value is a low-cardinality enum/bool safe to mirror as
// a GA user property. Other keys (free text, arrays) are never forwarded.
const USER_PROP_KEYS: ReadonlySet<keyof UserSettings> = new Set([
  'theme', 'tone', 'reducedMotion', 'privacyMode',
]);

export async function renderSettings(parent: HTMLElement) {
  clear(parent);
  const st = getState();
  const cur = st.settings;

  function row(label: string, control: HTMLElement) {
    return el('div', { attrs: { style: 'display:flex; align-items:center; justify-content:space-between; gap:1rem; padding:0.5rem 0;' } },
      el('div', {}, label),
      control,
    );
  }

  async function patch(p: Partial<UserSettings>) {
    const next: UserSettings = { ...cur, ...p };
    await settingsStore.set(next);
    for (const k of Object.keys(p) as (keyof UserSettings)[]) {
      track('setting_changed', { key: k });
      if (USER_PROP_KEYS.has(k)) {
        const v = p[k];
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          setUserProp(`app_${k}`, v);
        }
      }
    }
    toast('Saved.');
    window.dispatchEvent(new CustomEvent('wean:state-changed'));
  }

  const persisted = await isPersisted();
  const est = await estimateStorage();
  const usageMb = est.usage ? (est.usage / (1024 * 1024)).toFixed(2) : '–';
  const quotaMb = est.quota ? (est.quota / (1024 * 1024)).toFixed(0) : '–';

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Privacy & data'),
    row('Local data', el('span', { class: 'muted' }, 'Stays on this device.')),
    row('Privacy mode (neutral labels)', toggle(cur.privacyMode, (v) => patch({ privacyMode: v }))),
    row('Persistent storage',
      persisted
        ? el('span', { class: 'muted' }, 'Granted')
        : el('button', { class: 'btn', onclick: async () => {
            const ok = await requestPersistence();
            toast(ok ? 'Persistent storage granted.' : 'Browser declined. Backups still work.');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          } }, 'Request')),
    row('Storage used', el('span', { class: 'muted' }, `${usageMb} MB / ${quotaMb} MB`)),
    row('Backup reminders', toggle(cur.backupRemindersEnabled, (v) => patch({ backupRemindersEnabled: v }))),
    el('p', { class: 'muted', attrs: { style: 'font-size:0.8rem; margin:0;' } },
      'Off by default. When on, Today shows a nudge if no export in 14 days.'),
    el('a', { href: '#/backup', class: 'btn block', attrs: { style: 'margin-top:0.5rem;' } }, 'Open Backup'),
    el('a', { href: '#/resources', class: 'btn block', attrs: { style: 'margin-top:0.5rem;' } }, 'Support resources'),
  ));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Accessibility'),
    row('Theme', select(cur.theme, [
      { value: 'auto', label: 'Auto' },
      { value: 'dark', label: 'Dark' },
      { value: 'light', label: 'Light' },
      { value: 'high-contrast', label: 'High contrast' },
    ], (v) => { patch({ theme: v as UserSettings['theme'] }); applyTheme(v); })),
    row('Reduced motion', select(cur.reducedMotion, [
      { value: 'auto', label: 'Auto' }, { value: 'on', label: 'On' }, { value: 'off', label: 'Off' },
    ], (v) => patch({ reducedMotion: v as UserSettings['reducedMotion'] }))),
  ));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Tone'),
    row('Voice', select(cur.tone, [
      { value: 'warm', label: 'Warm' },
      { value: 'calm', label: 'Calm' },
      { value: 'playful', label: 'Playful' },
      { value: 'direct', label: 'Direct' },
      { value: 'minimal', label: 'Minimal' },
    ], (v) => patch({ tone: v as UserSettings['tone'] }))),
  ));

  parent.appendChild(displayCard());

  const notifSupported = notificationsSupported();
  const notifPerm = notifSupported ? Notification.permission : 'unsupported';
  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Notifications'),
    row('Browser notifications', el('span', { class: 'muted' }, notifSupported ? notifPerm : 'Unsupported')),
    (() => {
      const cb = el('input', {
        type: 'checkbox', checked: cur.notificationsEnabled,
        onchange: async (e: Event) => {
          const input = e.target as HTMLInputElement;
          const v = input.checked;
          if (v && notifSupported) {
            const perm = await requestPermission();
            if (perm !== 'granted') {
              toast('Browser denied notifications. Reminders stay in-app.');
              input.checked = false;
              await patch({ notificationsEnabled: false });
              return;
            }
          }
          await patch({ notificationsEnabled: v });
        },
      });
      return row('Enable target reminders', cb);
    })(),
    row('Target reached', toggle(cur.notifyTargetReached !== false,
      (v) => patch({ notifyTargetReached: v }))),
    row('Risk-window prep', toggle(cur.notifyRiskWindow === true,
      (v) => patch({ notifyRiskWindow: v }))),
    row('In-app only (no OS notifications)',
      toggle(cur.notificationsInAppOnly === true,
        (v) => patch({ notificationsInAppOnly: v }))),
    row('Quiet hours start', timeField(cur.quietHoursStart, (m) => patch({ quietHoursStart: m }))),
    row('Quiet hours end', timeField(cur.quietHoursEnd, (m) => patch({ quietHoursEnd: m }))),
    el('p', { class: 'muted', attrs: { style: 'font-size:0.8rem;' } },
      cur.privacyMode
        ? 'Privacy mode is on — reminders use generic copy.'
        : 'Reminders only fire while Wean is open. There is no backend push service.'),
  ));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Rescue defaults'),
    row('Default duration (sec)',
      el('input', { type: 'number', value: String(cur.defaultRescueDurationSec), min: '15', max: '600',
        onchange: (e: Event) => patch({ defaultRescueDurationSec: Number((e.target as HTMLInputElement).value) }) })),
  ));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Danger zone'),
    el('p', { class: 'muted' }, 'Wipes all local Wean data. Export a backup first if you want to restore.'),
    el('button', { class: 'btn danger', onclick: async () => {
      if (!confirm('Delete all local Wean data?')) return;
      await clearAll();
      toast('Local data cleared.');
      navigate('/');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    } }, 'Clear local data'),
  ));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Diagnostics'),
    el('p', { class: 'muted' }, 'Strips free-text content. Useful for bug reports.'),
    el('button', { class: 'btn', onclick: async () => { await downloadDiagnostics(); toast('Diagnostics downloaded.'); } }, 'Export diagnostics'),
  ));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'About'),
    el('div', {}, `Wean v${st.meta.app_version ?? '0.1.0'}`),
    el('div', { class: 'muted' }, 'Local-first. No accounts. No servers.'),
  ));
}

function displayCard(): HTMLElement {
  const standalone = isStandalone();
  const fs = isFullscreen();
  const installable = isInstallAvailable();

  const wrap = el('div', { class: 'card' },
    el('h2', {}, 'Display'),
  );

  // Status line
  const status = standalone
    ? 'Running in installed app mode — browser chrome already hidden.'
    : fs
      ? 'Currently fullscreen.'
      : 'Browser chrome visible.';
  wrap.appendChild(el('p', { class: 'muted', attrs: { style: 'margin: 0 0 0.75rem;' } }, status));

  if (!standalone) {
    if (fullscreenSupported()) {
      const fsBtn = el('button', { class: 'btn block', onclick: async () => {
        await toggleFullscreen();
        toast(isFullscreen() ? 'Fullscreen on.' : 'Fullscreen off.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } }, fs ? 'Exit fullscreen' : 'Enter fullscreen');
      wrap.appendChild(fsBtn);
    }
    if (installable) {
      wrap.appendChild(el('button', {
        class: 'btn primary block', attrs: { style: 'margin-top:0.5rem;' },
        onclick: async () => {
          const r = await promptInstall();
          if (r === 'accepted') toast('Installed.');
          else if (r === 'dismissed') toast('Install dismissed.');
          else toast('Install not available right now.');
        },
      }, 'Install Wean as app'));
    } else {
      wrap.appendChild(el('p', { class: 'muted', attrs: { style: 'font-size:0.8rem; margin-top:0.5rem;' } },
        'Tip: install Wean for a fullscreen, app-like experience. In Brave on Android tap ⋮ → "Add to Home screen". On iOS Safari, Share → "Add to Home Screen".'));
    }
  }

  return wrap;
}

function timeField(minutes: number | undefined, onChange: (m: number | undefined) => void): HTMLElement {
  const formatted = minutes !== undefined ? minutesToHHMM(minutes) : '';
  return el('input', {
    type: 'time',
    value: formatted,
    onchange: (e: Event) => {
      const v = (e.target as HTMLInputElement).value;
      if (!v) { onChange(undefined); return; }
      const [hStr, mStr] = v.split(':');
      const h = Number(hStr); const m = Number(mStr);
      if (Number.isFinite(h) && Number.isFinite(m)) onChange(h * 60 + m);
    },
  });
}

function minutesToHHMM(m: number): string {
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function toggle(value: boolean, onChange: (v: boolean) => void): HTMLElement {
  return el('input', { type: 'checkbox', checked: value, onchange: (e: Event) => onChange((e.target as HTMLInputElement).checked) });
}
function select<T extends string>(value: T, options: Array<{ value: T; label: string }>, onChange: (v: T) => void): HTMLElement {
  const s = el('select', { onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value as T) });
  for (const o of options) {
    const op = el('option', { value: o.value }, o.label);
    if (o.value === value) op.selected = true;
    s.appendChild(op);
  }
  return s;
}

export function applyTheme(theme: string) {
  const r = document.documentElement;
  r.classList.remove('theme-auto', 'theme-dark', 'theme-light', 'theme-hc');
  if (theme === 'auto') r.classList.add('theme-auto');
  else if (theme === 'dark') r.classList.add('theme-dark');
  else if (theme === 'light') r.classList.add('theme-light');
  else if (theme === 'high-contrast') r.classList.add('theme-hc');
}
